import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { NUM } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, AlertTriangle, Warehouse, Store } from "lucide-react";
import { StockSplit } from "@/components/StockSplit";

export const SOURCES = [
  ["depo", "Direkt depodan", "Tüm adetler depodan düşülür"],
  ["magaza_if_enough", "Mağaza yetiyorsa mağazadan", "Yetmeyen kalemin tamamı depodan"],
  ["magaza_first", "Önce mağaza, üstü depodan", "Mağazadaki kadar mağazadan, kalanı depodan"],
  ["manual", "Manuel dağıt", "Her kalem için mağaza adedini siz girin"],
];
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
// Sunucudaki magaza_share ile aynı kural
const magShare = (mode, qty, avail, manual) => {
  if (mode === "manual") return Math.min(Math.max(Number(manual) || 0, 0), qty);
  if (mode === "magaza_if_enough") return avail >= qty ? qty : 0;
  if (mode === "magaza_first") return Math.min(Math.max(avail, 0), qty);
  return 0;
};

function PlanRow({ p, manual, onManual }) {
  return (
    <div className={cn("rounded-lg border p-2 flex items-center gap-2 flex-wrap", p.short && "border-amber-400 bg-amber-50/60")} data-testid={`src-row-${p.idx}`}>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold leading-snug break-words">{p.it.name}</div>
        <div className="flex items-center gap-1.5 flex-wrap mt-0.5 text-[11px] text-muted-foreground">
          <span className="font-bold text-foreground tabular-nums">{NUM(p.qty)} {p.it.unit}</span><span>· mevcut</span>
          <StockSplit depo={p.dep0} magaza={p.mag0} unit={p.it.unit} testid={`src-avail-${p.idx}`} />
        </div>
      </div>
      {manual ? (
        <div className="flex items-center gap-1.5 shrink-0">
          <Store className="h-3.5 w-3.5 text-teal-600" />
          <Input type="number" inputMode="decimal" className="h-8 w-20 text-right tabular-nums" value={p.manualVal ?? ""} onChange={(e) => onManual(p.idx, e.target.value)} data-testid={`src-manual-${p.idx}`} />
          <span className="text-[11px] text-muted-foreground whitespace-nowrap"><Warehouse className="h-3.5 w-3.5 inline mr-0.5" />Depo <b className="text-foreground tabular-nums">{NUM(p.d)}</b></span>
        </div>
      ) : (
        <div className="flex items-center gap-1 shrink-0 text-[11px] font-bold tabular-nums" data-testid={`src-plan-${p.idx}`}>
          <span className="rounded bg-teal-50 text-teal-700 border border-teal-200 px-1.5">Mağaza {NUM(p.m)}</span>
          <span className="rounded bg-slate-100 text-slate-700 border border-slate-200 px-1.5">Depo {NUM(p.d)}</span>
        </div>
      )}
      {p.short && <div className="w-full text-[11px] font-semibold text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Seçilen bölümde yeterli stok yok — stok eksiye düşer</div>}
    </div>
  );
}

/** Teklif kabulünde stok kaynağı: depo · mağaza yetiyorsa · önce mağaza · manuel dağıtım. onConfirm({ stock_source, items }) */
export default function StockSourceDialog({ quote, onCancel, onConfirm }) {
  const lines = useMemo(() => (quote?.items || []).map((it, idx) => ({ it, idx })).filter(({ it }) => it.product_id && Number(it.quantity) > 0), [quote]);
  const [mode, setMode] = useState("depo");
  const [stock, setStock] = useState(null);
  const [man, setMan] = useState({});
  useEffect(() => {
    if (!quote) return;
    setMode(quote.stock_source || "depo");
    setMan(Object.fromEntries(lines.map(({ it, idx }) => [idx, it.mag_qty ?? 0])));
    setStock(null);
    const ids = [...new Set(lines.map(({ it }) => it.product_id))];
    if (!ids.length) { setStock({}); return; }
    api.get(`/products/stock-of?ids=${encodeURIComponent(ids.join(","))}`).then((r) => setStock(r.data || {})).catch(() => setStock({}));
  }, [quote, lines]);
  const plan = useMemo(() => {
    const used = {};
    return lines.map(({ it, idx }) => {
      const s = (stock || {})[it.product_id] || {};
      const u = used[it.product_id] || { m: 0, d: 0 };
      const mag0 = r2((Number(s.qty_magaza) || 0) - u.m);
      const dep0 = r2((Number(s.quantity) || 0) - (Number(s.qty_magaza) || 0) - u.d);
      const qty = Number(it.quantity) || 0;
      const m = r2(magShare(mode, qty, mag0, man[idx])), d = r2(qty - m);
      used[it.product_id] = { m: u.m + m, d: u.d + d };
      return { it, idx, qty, mag0, dep0, m, d, manualVal: man[idx], short: m > mag0 + 0.001 || d > dep0 + 0.001 };
    });
  }, [lines, stock, mode, man]);
  const tm = r2(plan.reduce((s, p) => s + p.m, 0)), td = r2(plan.reduce((s, p) => s + p.d, 0));
  const confirmIt = () => onConfirm({
    stock_source: mode,
    items: (quote.items || []).map((it, i) => (mode === "manual" && it.product_id && man[i] != null ? { ...it, mag_qty: Number(man[i]) || 0 } : it)),
  });

  return (
    <Dialog open={!!quote} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="stock-source-dialog">
        <DialogHeader><DialogTitle className="font-head text-xl">Stok nereden düşülsün?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">{quote?.quote_no} kabul edilince kalemler stoktan düşülür ve {quote?.customer_title} carisine işlenir.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {SOURCES.map(([v, lbl, desc]) => (
            <button key={v} type="button" onClick={() => setMode(v)} data-testid={`src-mode-${v}`}
              className={cn("rounded-xl border p-3 text-left transition-colors", mode === v ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted/50")}>
              <div className="font-bold text-sm">{lbl}</div><div className="text-[11px] text-muted-foreground">{desc}</div>
            </button>
          ))}
        </div>
        {stock === null ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div> : lines.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed rounded-lg p-4 text-center">Bu teklifte stoğa bağlı kalem yok — stok düşülmez.</p>
        ) : (
          <div className="space-y-1.5" data-testid="src-plan">
            {plan.map((p) => <PlanRow key={p.idx} p={p} manual={mode === "manual"} onManual={(i, v) => setMan((s) => ({ ...s, [i]: v }))} />)}
            <div className="flex justify-end gap-2 text-xs font-bold pt-1" data-testid="src-total">
              <span className="rounded-full bg-teal-50 text-teal-700 border border-teal-200 px-2.5 py-0.5">Mağazadan {NUM(tm)}</span>
              <span className="rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-2.5 py-0.5">Depodan {NUM(td)}</span>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="src-cancel">Vazgeç</Button>
          <Button onClick={confirmIt} disabled={stock === null} data-testid="src-confirm"><CheckCircle2 className="h-4 w-4 mr-2" />Kabul Et ve Stoktan Düş</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
